﻿module Illy.SafeStack.Client.NumberPicker

open Fable.React
open Fable.React.Props
open Fable.Core.JsInterop
open Elmish.React
open Illy.SafeStack
open Illy.SafeStack.Client
open Elmish
open System
open ROP

type PickerType =
    | Int
    | Decimal

let parseNumberOption =
    function
    | PickerType.Int, None -> ParsedNumber.Int ParsedInt.Empty
    | PickerType.Int, Some (Number.Int i) -> ParsedNumber.Int (ParsedInt.Valid i)
    | PickerType.Decimal, None -> ParsedNumber.Decimal ParsedDecimal.Empty
    | PickerType.Decimal, Some (Number.Decimal i) -> ParsedNumber.Decimal (ParsedDecimal.Valid i)
    | _ -> failwith "Invalid picker type vs initial number type"

type CustomSettings = 
    {
        IsMandatory: bool
        Type: PickerType
        Step: Number
        Max: Number option
        Min: Number option
        AllowNegative: bool
    }

module CustomSettings = 
    let defaultOptions = 
        {
            IsMandatory = false
            AllowNegative = false
            Type = PickerType.Int
            Step = Number.Int 1
            Max = None
            Min = None
        }

type Control =
    {
        Id: string
        Label: string
        Placeholder: string option
        Type: PickerType
        NumberString: string //used for the up & down buttons only
        Number: ParsedNumber
        IsFocused: bool
        UpDownButtonsDisabled: bool
        Error: string option 
        Warning: string option
    }

type Model = ControlState<Control, CustomSettings>

type Msg =
    | SetNumber of string
    | IncrementNumber
    | DecrementNumber
    | SetNumberPickerFocus of hasFocus: bool


let private initControl id label placeholder t (number: Number option) = 
    let num = parseNumberOption (t,number)
    {
        Id = id
        Label = label
        Placeholder = placeholder
        NumberString = ParsedNumber.format num
        Number = num
        IsFocused = false
        UpDownButtonsDisabled = false
        Error = None
        Warning = None
        Type = t
    }

let init id label placeholder (customSettings: CustomSettings) number : Model =
    {
        Control = initControl id label placeholder customSettings.Type number
        Settings = ControlSettings.defaultOptions
        CustomSettings = customSettings
    }

let parsedOrDefault = ParsedInt.toOption >> Option.defaultValue 0 
let setToZeroIfNegative x = if x < 0 then 0 else x 

let private focusTextInput id = 
    let el = Browser.Dom.document.getElementById(id)

    if isNull el then ()
    else 
        el.focus()

let private blurTextInput id = 
    let el = Browser.Dom.document.getElementById(id)

    if isNull el then ()
    else 
        el.blur()

let private parse str (settings: CustomSettings) =
    match settings.Type with
    | PickerType.Int -> ParsedInt.ofString str |> fun (x,y) -> ParsedNumber.Int x,y
    | PickerType.Decimal -> ParsedDecimal.ofString str |> fun (x,y) -> ParsedNumber.Decimal x,y

let private checkNegative custom num =
    if custom.AllowNegative then
        ROP.succeed num
    else
        let (isNeg,_) = Number.preventNegative num
        if isNeg then
            ROP.fail "Negative numbers are not valid."
        else
            ROP.succeed num
        

let private checkMin custom num =
    match custom.Min with
    | None -> ROP.succeed num
    | Some min when min <= num -> ROP.succeed num
    | _ -> ROP.fail "The number is below the minimum value."

let private checkMax custom num =
    match custom.Max with
    | None -> ROP.succeed num
    | Some max when max >= num -> ROP.succeed num
    | _ -> ROP.fail "The number is above the maximum value."
   

let private checkNumber custom num =
    checkNegative custom num
    >>= checkMin custom
    >>= checkMax custom

let private setNumber model newNumber =
    match checkNumber model.CustomSettings newNumber with
    | ROP.Success num -> num, None
    | ROP.Failure rsn -> newNumber, Some rsn

let update (msg: Msg) model: Model * Cmd<Msg> =    
    let updateControl updateFunc = 
        {model with Control = updateFunc model.Control}

    match msg with 
    | SetNumber str -> 
        let p, v = parse str model.CustomSettings
        
        if v.IsSome || ParsedNumber.isNone p then 
            updateControl
                (fun x -> 
                    { x with
                        NumberString = str
                        Number = p
                        Error = v
                    }
                ), []
        else 
            let upDownButtonsEnabled = ParsedNumber.isNone p = false || String.IsNullOrEmpty str
            let num, err = setNumber model (ParsedNumber.coorce p)

            updateControl 
                (fun x -> 
                    { x with 
                        NumberString = Number.format num
                        Number = ParsedNumber.ofNumber num
                        Error = err
                        UpDownButtonsDisabled = not upDownButtonsEnabled
                    }), []
    
    | IncrementNumber -> 
        focusTextInput model.Control.Id
        
        let p, validations = parse model.Control.NumberString model.CustomSettings

        if validations.IsSome then 
            model, []
        else 
            let newNumber = ParsedNumber.coorce p + model.CustomSettings.Step
            let num, err = setNumber model newNumber
            updateControl 
                (fun x -> 
                    {x with 
                        Number = ParsedNumber.ofNumber num
                        NumberString = Number.format num
                        Error = err
                    }), []
    
    | DecrementNumber -> 
        focusTextInput model.Control.Id
        
        let p, validations = parse model.Control.NumberString model.CustomSettings

        if validations.IsSome then 
            model, []
        else 
            let newNumber = ParsedNumber.coorce p - model.CustomSettings.Step
            let num, err = setNumber model newNumber
            updateControl 
                (fun x -> 
                    {x with 
                        Number = ParsedNumber.ofNumber num
                        NumberString = Number.format num
                        Error = err
                    }), []
    | SetNumberPickerFocus hasFocus -> 
        if not hasFocus then //reset focus
            blurTextInput model.Control.Id
            updateControl (fun x -> {x with IsFocused = false}), []
        else
            focusTextInput model.Control.Id
            updateControl (fun x -> {x with IsFocused = true}), []


let numberInput loading model dispatch=
    let control = model.Control
    let value = control.Number |> ParsedNumber.format
    let debounceFocus = debounce 150 (fun (msg, value) -> dispatch (msg value)) SetNumberPickerFocus

    div [ Class (sprintf "number-picker-container %s" (if control.IsFocused then "focused" else ""))] [
        div [Class "number-picker-input-container"] [
            input [ 
                Id control.Id 
                Class (jQueryUI.addSkeletonScreen "form-control" None loading)
                Props.Type "text"
                OnChange (fun ev -> dispatch (SetNumber !!ev.target?value))
                OnKeyDown 
                    (fun ev -> 
                        if not control.UpDownButtonsDisabled then
                            if ev.key = "ArrowUp" // up arrow
                            then 
                                ev.preventDefault()
                                dispatch IncrementNumber
                            
                            else if ev.key = "ArrowDown" // up arrow
                            then 
                                ev.preventDefault()
                                dispatch DecrementNumber
                    )
                OnFocus
                    (fun _ ->
                        if control.IsFocused then debounceFocus true
                        else SetNumberPickerFocus true |> dispatch
                    )
                OnBlur (fun _ -> debounceFocus false)
                Placeholder (match control.Placeholder with Some pl when not loading -> pl | _ -> "" )
                MaxLength 10.
                Helpers.valueOrDefault value
                Disabled loading
            ] 
        ]
        div [ Class ("number-picker-buttons" + if control.UpDownButtonsDisabled then " disabled" else "")] [
            FontAwesome.solid "caret-up" [FontAwesome.Modifier.Fixed] [
                OnClick (fun _ -> if not control.UpDownButtonsDisabled then dispatch IncrementNumber)
            ]

            FontAwesome.solid "caret-down" [FontAwesome.Modifier.Fixed] [
                OnClick (fun _ -> if not control.UpDownButtonsDisabled then dispatch DecrementNumber)
            ]
        ]
    ]

let view loading (model: Model) (dispatch: Msg -> unit) = 
    let control = model.Control
    
    jQueryUI.inputGroup [
        jQueryUI.labelWithMandatory control.Id control.Label control.Error control.Warning loading model.CustomSettings.IsMandatory
        numberInput loading model dispatch
    ]

//helper functions
let resetValidations (model: Model) = 
    {model with Control = {model.Control with Error = None; Warning = None}}

let setValidations (model: Model) errors warnings = 
    {model with Control = {model.Control with Error = errors; Warning = warnings}}

let toOption (model: Model) =
    model.Control.Number |> ParsedNumber.toNumber

let setSelectedOption (model: Model) num =
    { model with 
        Control = 
            { model.Control with 
                Number = ParsedNumber.ofNumber num
                NumberString = Number.format num
            }}

let resetSelectedOption (model: Model) =
    match model.CustomSettings.Type with 
    | PickerType.Int -> setSelectedOption model (Number.Int 0)
    | PickerType.Decimal -> setSelectedOption model (Number.Decimal 0m)